import React, { useEffect } from 'react'
import { Box, Divider, Drawer, Typography } from '@mui/material'
import { IconX } from '@tabler/icons-react'
import useAppUtilities from '../../hooks/useAppUtilities'
import { PredictedDish } from '../../types/menuTypes'
import DishDetailCommon from '../restaurants/DishDetailCommon'
import ImgIcon from '../ui/ImgIcon'
import TagChip from '../ui/TagChip'
import useRudderStackAnalytics from '../../useRudderAnalytics'
import { useAppSelector } from '../../store'
import { selectLogin } from '../../store/authSlice'
import { logEvent } from '../../analytics/analytics'

interface RestaurantDrawerProps {
  open: boolean
  toggleDrawer: React.Dispatch<React.SetStateAction<boolean>>
  data: Partial<PredictedDish>
  restaurantAlias: string
  /** Restaurant UUID — preferred as restaurant_id for analytics (alias is a slug, not a stable id). */
  restaurantId?: string
}

const RestaurantDrawer: React.FC<RestaurantDrawerProps> = ({
  open,
  toggleDrawer,
  data,
  restaurantAlias,
  restaurantId,
}) => {
  const { theme, isSmallScreen } = useAppUtilities()
  const userDetail = useAppSelector(selectLogin)
  const [analytics, isAnalyticsReady] = useRudderStackAnalytics()
  const [imageLoadError, setImageLoadError] = React.useState(false)

  React.useEffect(() => {
    setImageLoadError(false)
  }, [data?.dish?.imageUrl])

  useEffect(() => {
    if (analytics && isAnalyticsReady) {
      analytics?.track('DISH_PAGE', {
        category: 'DISH',
        opened: open,
        eaterId: `${userDetail?.eaterDTO?.eaterId ?? ''}`,
        eaterName: `${userDetail?.eaterDTO?.firstName ?? ''} ${
          userDetail?.eaterDTO?.lastName ?? ''
        }`,
        eaterEmail: `${userDetail?.eaterDTO?.email ?? ''}`,
        dishName: `${data?.dish?.name ?? ''}`,
        dishId: `${data?.dish?.dishID ?? ''}`,
        menuName: `${data?.menuName ?? ''}`,
        menuId: `${data?.menuId ?? ''}`,
        restaurantAlias: `${restaurantAlias}`,
        startTime: new Date().toISOString(),
      })
    }
    
    // Track dish_click when drawer opens (opening is the result of a dish tap).
    // Prefer the restaurant UUID; fall back to the alias only if the UUID wasn't threaded through.
    if (open && data?.dish) {
      logEvent('dish_click', {
        restaurant_id: restaurantId ?? restaurantAlias,
        dish_id: data.dish.dishID ?? '',
        screen_name: 'restaurant_detail',
      })
    }
  }, [open, analytics, isAnalyticsReady, data, restaurantAlias, restaurantId])

  return (
    <Drawer
      open={open}
      onClose={() => toggleDrawer(false)}
      anchor={isSmallScreen ? 'bottom' : 'right'}
      sx={{
        '& .MuiPaper-root': {
          borderTopLeftRadius: isSmallScreen ? '16px' : 'none',
          borderTopRightRadius: isSmallScreen ? '16px' : 'none',
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 'calc(100vw - 5px)', md: '320px' },
          height: '100%',
        }}
      >
        <div style={{ padding: '16px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" color="text.primary">
              Details
            </Typography>
            <ImgIcon
              iconElement={<IconX stroke={2} style={{ margin: '7px' }} />}
              alt="close-drawer"
              wrapperStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: '50%',
                height: '32px',
                width: '32px',
                cursor: 'pointer',
              }}
              onClick={() => toggleDrawer(false)}
            />
          </div>
        </div>
        <Divider
          orientation="horizontal"
          flexItem
          sx={{ color: theme.palette.grey[300] }}
        />
        <div style={{ padding: '16px' }}>
          <div
            style={{
              position: 'relative',
            }}
          >
            {data?.dish?.imageUrl &&
            data.dish.imageUrl.length !== 0 &&
            data.dish.imageUrl !== '/logoLetter-resize.svg' &&
            !imageLoadError ? (
              <img
                src={data.dish.imageUrl}
                alt={`${data?.dish?.name ?? 'dish'}`}
                style={{
                  width: '100%',
                  height: isSmallScreen ? '200px' : '160px',
                  borderRadius: '16px',
                  objectFit: 'cover',
                }}
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: isSmallScreen ? '200px' : '160px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #FFF5ED 0%, #FFE8D4 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  opacity={0.35}
                >
                  <path
                    d="M18 8h1a4 4 0 0 1 0 8h-1"
                    stroke={theme.palette.primary.main}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"
                    stroke={theme.palette.primary.main}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="6"
                    y1="1"
                    x2="6"
                    y2="4"
                    stroke={theme.palette.primary.main}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="10"
                    y1="1"
                    x2="10"
                    y2="4"
                    stroke={theme.palette.primary.main}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="14"
                    y1="1"
                    x2="14"
                    y2="4"
                    stroke={theme.palette.primary.main}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </Box>
            )}
            {data.probability && data.probability.yes !== 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '6px',
                  left: '6px',
                  zIndex: 100,
                }}
              >
                <TagChip
                  data={[
                    {
                      percentage: `${data?.probability?.yes.toFixed(2)}`,
                      title: 'Match',
                    },
                  ]}
                  bgColor="liteOrange"
                />
              </div>
            )}
          </div>
          <div style={{ paddingTop: '20px' }}>
            <Typography variant="body1" textTransform="capitalize">
              {data?.dish?.name?.toLocaleLowerCase()}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: theme.palette.grey[500],
                paddingRight: '10px',
              }}
            >
              {data?.dish?.description}
            </Typography>
          </div>
          {data?.dish?.price && (
            <div style={{ paddingTop: '20px' }}>
              <Typography variant="body2">{data?.dish?.price}</Typography>
            </div>
          )}
          <DishDetailCommon
            Ingredients={data?.dish?.ingredients}
            suitableDietary={data?.dish?.dietary}
            unsuitableDietary={data?.dish?.unsuitabledietary}
          />
        </div>
      </Box>
    </Drawer>
  )
}

export default RestaurantDrawer
